import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Chip,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { listStudentAdvanceEntries } from '../utils/canteenLedger';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString();
};

const ManageAdvanceDialog = ({
  open,
  onClose,
  student,
  asOfDate,
  currencySymbol = '₵',
  loading = false,
  onRemoveMealDates,
  onSaveEdit,
}) => {
  const [selectedMealDates, setSelectedMealDates] = useState([]);
  const [editMealDate, setEditMealDate] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editPaymentDate, setEditPaymentDate] = useState('');

  const entries = useMemo(() => {
    if (!student || !open) return [];
    return listStudentAdvanceEntries(student, asOfDate);
  }, [student, asOfDate, open]);

  useEffect(() => {
    if (!open) {
      setSelectedMealDates([]);
      setEditMealDate(null);
      setEditAmount('');
      setEditPaymentDate('');
    }
  }, [open]);

  const studentName = student
    ? `${student.lastname || ''} ${student.othernames || ''}`.trim()
    : '';

  const toggleMealDate = (mealDate) => {
    setSelectedMealDates((prev) =>
      prev.includes(mealDate)
        ? prev.filter((d) => d !== mealDate)
        : [...prev, mealDate]
    );
  };

  const toggleAll = (checked) => {
    setSelectedMealDates(checked ? entries.map((e) => e.serviceDate) : []);
  };

  const startEdit = (row) => {
    setEditMealDate(row.serviceDate);
    setEditAmount(String(row.amount));
    setEditPaymentDate(row.paymentDate || asOfDate || '');
  };

  const cancelEdit = () => {
    setEditMealDate(null);
    setEditAmount('');
    setEditPaymentDate('');
  };

  const handleSaveEdit = async () => {
    if (!editMealDate || !onSaveEdit) return;
    const amount = parseFloat(editAmount);
    if (!amount || amount <= 0) return;
    const ok = await onSaveEdit(editMealDate, amount, editPaymentDate);
    if (ok) cancelEdit();
  };

  const handleRemoveOne = (mealDate) => {
    onRemoveMealDates?.([mealDate]);
  };

  const handleRemoveSelected = () => {
    if (selectedMealDates.length === 0) return;
    onRemoveMealDates?.(selectedMealDates);
  };

  const handleRemoveBatch = (paymentDate) => {
    const dates = entries
      .filter((e) => e.paymentDate === paymentDate)
      .map((e) => e.serviceDate);
    if (dates.length === 0) return;
    onRemoveMealDates?.(dates);
  };

  const batchPaymentDates = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const e of entries) {
      if (e.paymentDate && !seen.has(e.paymentDate)) {
        seen.add(e.paymentDate);
        out.push(e.paymentDate);
      }
    }
    return out;
  }, [entries]);

  const allSelected =
    entries.length > 0 &&
    entries.every((e) => selectedMealDates.includes(e.serviceDate));
  const someSelected =
    entries.some((e) => selectedMealDates.includes(e.serviceDate)) &&
    !allSelected;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        Manage advance payments
        {studentName ? ` — ${studentName}` : ''}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {entries.length === 0 ? (
          <Typography color="text.secondary">
            No advance or future prepaid entries for this student.
          </Typography>
        ) : (
          <>
            {batchPaymentDates.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Remove batch by cash date:
                </Typography>
                {batchPaymentDates.map((pd) => (
                  <Button
                    key={pd}
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={loading}
                    onClick={() => handleRemoveBatch(pd)}
                  >
                    {formatDate(pd)} (
                    {entries.filter((e) => e.paymentDate === pd).length} days)
                  </Button>
                ))}
              </Stack>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell>Meal date</TableCell>
                    <TableCell>Cash date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((row) => {
                    const isEditing = editMealDate === row.serviceDate;
                    return (
                      <TableRow key={row.serviceDate} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedMealDates.includes(row.serviceDate)}
                            onChange={() => toggleMealDate(row.serviceDate)}
                            disabled={loading || isEditing}
                          />
                        </TableCell>
                        <TableCell>{formatDate(row.serviceDate)}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              type="date"
                              size="small"
                              value={editPaymentDate}
                              onChange={(e) => setEditPaymentDate(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              disabled={loading}
                            />
                          ) : (
                            formatDate(row.paymentDate)
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editAmount}
                              onChange={(e) => setEditAmount(e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                              disabled={loading}
                            />
                          ) : (
                            `${currencySymbol} ${row.amount.toFixed(2)}`
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status}
                            color={row.isAdvance ? 'warning' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleSaveEdit}
                                disabled={loading}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                onClick={cancelEdit}
                                disabled={loading}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                color="primary"
                                title="Edit"
                                onClick={() => startEdit(row)}
                                disabled={loading || !!editMealDate}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="warning"
                                title="Remove"
                                onClick={() => handleRemoveOne(row.serviceDate)}
                                disabled={loading || !!editMealDate}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          color="warning"
          variant="contained"
          onClick={handleRemoveSelected}
          disabled={loading || selectedMealDates.length === 0}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Remove selected ({selectedMealDates.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageAdvanceDialog;
